import { convex } from "@/lib/convexClient";
import { ClientS3Manager } from "@/services/s3/ClientS3Manager";
import { EncryptedS3CredentialService } from "@/services/s3/EncryptedS3CredentialService";
import { SecureCredentialStorage } from "@/services/s3/SecureCredentialStorage";
import { UnifiedS3CredentialService } from "@/services/s3/UnifiedS3CredentialService";
import { StorageService as CoreStorageService } from "@/services/storageService";
import { api } from "../../../convex/_generated/api";
import { isElectron, LocalFileSystemService } from "./LocalFileSystemClient";

export interface StorageService {
  uploadFile(file: File, path: string): Promise<string>;
  deleteFile(path: string): Promise<void>;
  getFileUrl(path: string): Promise<string>;
  generateUploadUrl(path: string, contentType: string): Promise<string>;
  listFiles(prefix: string): Promise<string[]>;
  testConnection(): Promise<{ success: boolean; error?: string }>;
}

export class CloudStorageService implements StorageService {
  private craftspaceId?: string;

  constructor(craftspaceId?: string) {
    this.craftspaceId = craftspaceId;
  }

  async uploadFile(file: File, path: string): Promise<string> {
    const { data } = await CoreStorageService.uploadPrivate("staging", path, file);
    return data.path;
  }

  async deleteFile(path: string): Promise<void> {
    await CoreStorageService.deleteFile("staging", path);
  }

  async getFileUrl(path: string): Promise<string> {
    const { data } = await CoreStorageService.getPrivateUrl("staging", path, 3600, this.craftspaceId);
    return data.signedUrl;
  }

  async generateUploadUrl(path: string, _contentType: string): Promise<string> {
    const { data } = await CoreStorageService.createSignedUploadUrl("staging", path);
    return data.signedUrl;
  }

  async listFiles(prefix: string): Promise<string[]> {
    const data = await CoreStorageService.listFiles("staging", prefix);

    // Recursively get all files
    const allFiles: string[] = [];

    type StorageEntry = { name?: string; metadata?: { size?: number } };
    const processItems = async (items: StorageEntry[], currentPath: string) => {
      for (const item of items) {
        const fullPath = currentPath ? `${currentPath}/${item.name}` : item.name;

        if (item.metadata?.size !== undefined) {
          // It's a file
          allFiles.push(fullPath);
        } else {
          // It's a folder, recurse
          const subItems = await CoreStorageService.listFiles("staging", fullPath).catch(
            () => null,
          );
          if (subItems) {
            await processItems(subItems, fullPath);
          }
        }
      }
    };

    await processItems(data, prefix);
    return allFiles;
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      // Test by trying to list files
      await CoreStorageService.listFiles("staging", "");
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      return { success: false, error: message };
    }
  }
}

export class S3StorageService implements StorageService {
  private s3Manager: ClientS3Manager;

  constructor(s3Manager: ClientS3Manager) {
    this.s3Manager = s3Manager;
  }

  async uploadFile(file: File, path: string): Promise<string> {
    return await this.s3Manager.uploadFile(file, path);
  }

  async deleteFile(path: string): Promise<void> {
    await this.s3Manager.deleteFile(path);
  }

  async getFileUrl(path: string): Promise<string> {
    return await this.s3Manager.generateDownloadUrl(path);
  }

  async generateUploadUrl(path: string, contentType: string): Promise<string> {
    return await this.s3Manager.generateUploadUrl(path, contentType);
  }

  async listFiles(prefix: string): Promise<string[]> {
    return await this.s3Manager.listFiles(prefix);
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    return await this.s3Manager.testConnection();
  }
}

export class StorageServiceFactory {
  // Cache for craftspace storage providers to reduce database queries
  private static storageProviderCache = new Map<
    string,
    { provider: "360crafter" | "custom-s3"; timestamp: number }
  >();
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private static isLikelyLocalId(id: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      id,
    );
  }

  static async create(craftspaceId: string, password?: string): Promise<StorageService> {
    return StorageServiceFactory.createStorageService(craftspaceId, password);
  }

  static async createStorageService(
    craftspaceId: string,
    password?: string,
  ): Promise<StorageService> {
    try {
      // In Electron, local UUID craftspaces use local filesystem; cloud craftspaces stay cloud-backed.
      if (isElectron() && StorageServiceFactory.isLikelyLocalId(craftspaceId)) {
        return new LocalFileSystemService();
      }

      // If craftspaceId is 'default', use default storage
      if (craftspaceId === "default") {
        return new CloudStorageService();
      }

      // Get storage provider (with caching)
      const storageProvider = await StorageServiceFactory.getCachedStorageProvider(craftspaceId);

      if (storageProvider === "custom-s3") {
        // Try to get credentials from database (handles both encrypted and unencrypted)
        const credentials = await UnifiedS3CredentialService.getCredentials(craftspaceId);

        if (credentials) {
          try {
            // Test S3 connection before using it
            const s3Manager = new ClientS3Manager(credentials);
            const testResult = await s3Manager.testConnection();

            if (testResult.success) {
              return new S3StorageService(s3Manager);
            } else {
              console.warn("⚠️ S3 connection failed, using 360Crafter cloud storage");
              return new CloudStorageService(craftspaceId);
            }
          } catch (_s3Error) {
            console.warn("⚠️ S3 setup failed, using 360Crafter cloud storage");
            return new CloudStorageService(craftspaceId);
          }
        } else if (password) {
          // Fallback to old localStorage method for backward compatibility
          try {
            const oldCredentials = await SecureCredentialStorage.getCredentials(
              craftspaceId,
              password,
            );
            if (oldCredentials) {
              // Test the old credentials before using them
              const s3Manager = new ClientS3Manager(oldCredentials);
              const testResult = await s3Manager.testConnection();

              if (testResult.success) {
                // Migrate old credentials to encrypted storage
                await EncryptedS3CredentialService.storeCredentials(craftspaceId, oldCredentials);
                return new S3StorageService(s3Manager);
              } else {
                console.warn("⚠️ Old S3 credentials failed, using 360Crafter cloud storage");
                return new CloudStorageService(craftspaceId);
              }
            }
          } catch (_oldCredsError) {
            console.warn("⚠️ Old S3 credentials setup failed, using 360Crafter cloud storage");
            return new CloudStorageService(craftspaceId);
          }
        }

        console.warn("⚠️ No S3 credentials found, using 360Crafter cloud storage");
        return new CloudStorageService(craftspaceId);
      } else {
        return new CloudStorageService(craftspaceId);
      }
    } catch (error) {
      console.error("Error in createStorageService, using default storage:", error);
      return new CloudStorageService();
    }
  }

  /**
   * Get cached storage provider for a craftspace
   */
  private static async getCachedStorageProvider(
    craftspaceId: string,
  ): Promise<"360crafter" | "custom-s3"> {
    // Check cache first
    const cached = StorageServiceFactory.storageProviderCache.get(craftspaceId);
    if (cached && Date.now() - cached.timestamp < StorageServiceFactory.CACHE_DURATION) {
      return cached.provider;
    }

    // If not in cache or expired, fetch from database
    const provider = await StorageServiceFactory.fetchStorageProviderFromDB(craftspaceId);

    // Update cache
    StorageServiceFactory.storageProviderCache.set(craftspaceId, {
      provider,
      timestamp: Date.now(),
    });

    return provider;
  }

  /**
   * Fetch storage provider from database
   */
  private static async fetchStorageProviderFromDB(
    craftspaceId: string,
  ): Promise<"360crafter" | "custom-s3"> {
    try {
      // Local default/UUID craftspaces are not Convex craftspace IDs.
      if (
        craftspaceId === "default" ||
        (isElectron() && StorageServiceFactory.isLikelyLocalId(craftspaceId))
      ) {
        return "360crafter";
      }

      const storageProvider = await convex.query(api.craftspaces.getCraftspaceStorageProvider, {
        id: craftspaceId,
      });

      return storageProvider === "custom-s3" ? "custom-s3" : "360crafter";
    } catch (error) {
      console.error("Error in fetchStorageProviderFromDB:", error);
      // Return default provider on any error
      return "360crafter";
    }
  }

  static async getCraftspaceStorageProvider(
    craftspaceId: string,
  ): Promise<"360crafter" | "custom-s3" | null> {
    try {
      return await StorageServiceFactory.getCachedStorageProvider(craftspaceId);
    } catch (error) {
      console.error("Error in getCraftspaceStorageProvider:", error);
      // Return default provider on any error
      return "360crafter";
    }
  }

  static async hasStoredCredentials(craftspaceId: string): Promise<boolean> {
    return UnifiedS3CredentialService.hasValidCredentials(craftspaceId);
  }

  /**
   * Clear the storage provider cache for a specific craftspace or all craftspaces
   */
  static clearStorageProviderCache(craftspaceId?: string): void {
    if (craftspaceId) {
      StorageServiceFactory.storageProviderCache.delete(craftspaceId);
    } else {
      StorageServiceFactory.storageProviderCache.clear();
    }
  }

  static async getCredentialAge(_craftspaceId: string): Promise<number | null> {
    // For encrypted credentials, we don't track age - they're always valid
    // This method is kept for backward compatibility
    return null;
  }

  static async clearCredentials(craftspaceId: string): Promise<void> {
    await UnifiedS3CredentialService.removeCredentials(craftspaceId);
  }
}
